/**
 * Personal Telegram Signal — confirmation modal.
 *
 * "Signal to send" selector (2026-05-05):
 *   - Default pick (pre-populated primary from signal_quality)
 *   - Under option (dropdown of every real-book UNDER line)
 *   - Handicap option (dropdown of every real-book HC -1.5 side)
 * The operator never sees theoretical lines — only picks the
 * bookmaker actually prices at the moment of the refresh.
 *
 * The selected option drives market/side/line/odds/probability_used
 * sent to `/api/tt/admin/personal-signal/send/{match_id}`.
 * EV/Kelly are recomputed client-side (pure math, no LLM).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { adminPost } from '../data/api';
import { Send, X, AlertTriangle } from 'lucide-react';

const fmtPct = (v) =>
  v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`;
const fmtNum = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

const OPTIONS = [
  { key: 'default', label: 'Default pick' },
  { key: 'under', label: 'Under option' },
  { key: 'handicap', label: 'Handicap option' },
];

/** Pure EV% = (prob * odds - 1) * 100. */
function computeEvPct(prob, odds) {
  if (prob == null || odds == null) return null;
  const p = Number(prob);
  const o = Number(odds);
  if (!Number.isFinite(p) || !Number.isFinite(o) || o <= 1.01) return null;
  return (p * o - 1.0) * 100.0;
}

/** Full Kelly fraction — 0 when EV ≤ 0. */
function computeKellyFull(prob, odds) {
  const p = Number(prob);
  const o = Number(odds);
  if (!Number.isFinite(p) || !Number.isFinite(o) || o <= 1.01) return null;
  if (p <= 0 || p >= 1) return 0;
  const k = (p * o - 1.0) / (o - 1.0);
  return Math.max(0, k);
}

export default function PersonalSignalModal({ open, form, pin, onClose, onSent }) {
  const [stake, setStake] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [redConfirmed, setRedConfirmed] = useState(false);
  const [selectedOption, setSelectedOption] = useState('default');
  // index into alt_candidates.UNDER / alt_candidates.HC
  const [underIdx, setUnderIdx] = useState(0);
  const [hcIdx, setHcIdx] = useState(0);

  const alt = form?.signal_quality?.alt_candidates || {};
  const underList = useMemo(() => alt.UNDER || [], [alt.UNDER]);
  const hcList = useMemo(() => alt.HC || [], [alt.HC]);

  useEffect(() => {
    if (open && form) {
      const initial = form.stake_units ?? 0.5;
      setStake(String(Number(initial).toFixed(2)));
      setNote('');
      setErr(null);
      setRedConfirmed(false);
      setSelectedOption('default');
      // Default the Under dropdown to the line closest to the primary
      // OU pick if the default is already OU, else first available.
      if (form.market === 'OU' && form.line != null && underList.length) {
        const idx = underList.findIndex(
          (c) => Math.abs((c.line || 0) - Number(form.line)) < 1e-6,
        );
        setUnderIdx(idx >= 0 ? idx : 0);
      } else {
        setUnderIdx(0);
      }
      // Default the HC dropdown to the primary side if it's HC
      if (form.market === 'HC' && form.side && hcList.length) {
        const idx = hcList.findIndex((c) => c.side === form.side);
        setHcIdx(idx >= 0 ? idx : 0);
      } else {
        setHcIdx(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  // Derive the effective pick based on the selected option.
  const effective = useMemo(() => {
    if (!form) return null;
    if (selectedOption === 'under') {
      const c = underList[underIdx];
      if (!c) return null;
      return {
        market: 'OU',
        side: c.side,
        line: c.line,
        odds: c.odds,
        probability_used: c.probability_used,
      };
    }
    if (selectedOption === 'handicap') {
      const c = hcList[hcIdx];
      if (!c) return null;
      return {
        market: 'HC',
        side: c.side,
        line: c.line,
        odds: c.odds,
        probability_used: c.probability_used,
      };
    }
    return {
      market: form.market,
      side: form.side,
      line: form.line,
      odds: form.odds,
      probability_used: form.probability_used,
    };
  }, [selectedOption, underIdx, hcIdx, form, underList, hcList]);

  const evPct = effective
    ? computeEvPct(effective.probability_used, effective.odds)
    : null;
  const kellyFull = effective
    ? computeKellyFull(effective.probability_used, effective.odds)
    : null;
  const kellyFractionPct =
    form?.signal_quality?.kelly_fraction_pct != null
      ? Number(form.signal_quality.kelly_fraction_pct)
      : 0;
  const suggestedUnits =
    kellyFull != null
      ? Math.min(1.0, Math.max(0, kellyFull * (kellyFractionPct / 100) * 100))
      : null;

  if (!open || !form) return null;
  const isRed = !!form.is_red;
  const evNegative = evPct != null && evPct <= 0;
  const underDisabled = selectedOption === 'under' && underList.length === 0;
  const hcDisabled = selectedOption === 'handicap' && hcList.length === 0;

  const handleSend = async () => {
    setErr(null);
    if (isRed && !redConfirmed) {
      setErr('RED badge — confirm extra step.');
      return;
    }
    if (!effective || effective.odds == null || effective.probability_used == null) {
      setErr('No real book odds available for the selected option.');
      return;
    }
    const stakeNum = parseFloat(stake);
    if (Number.isNaN(stakeNum) || stakeNum < 0) {
      setErr('Stake must be ≥ 0');
      return;
    }
    setBusy(true);
    try {
      const r = await adminPost(
        `/tt/admin/personal-signal/send/${form.match_id}`,
        pin,
        {
          market: effective.market,
          side: effective.side,
          line: effective.line,
          odds: effective.odds,
          probability_used: effective.probability_used,
          stake_units: stakeNum,
          signal_quality: {
            ...form.signal_quality,
            // override scalar fields with the user-selected candidate so
            // the Telegram payload + `tt_personal_signals` doc reflect
            // the actual pick sent, not the original default.
            signal_type:
              effective.market === 'OU'
                ? String(effective.side || '').startsWith('OVER')
                  ? 'OVER'
                  : 'UNDER'
                : effective.market,
            side: effective.side,
            line: effective.line,
            odds: effective.odds,
            probability_used: effective.probability_used,
            ev_pct: evPct,
            kelly_full_pct: kellyFull != null ? kellyFull * 100 : null,
            kelly_suggested_units: suggestedUnits,
          },
          operator_note: note ? note.trim() : null,
        },
      );
      if (r.status === 'error') {
        setErr(r.error || 'send failed');
      } else {
        onSent?.(r.signal);
        onClose();
      }
    } catch (e) {
      setErr(e?.message || 'request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="personal-signal-modal"
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-md max-w-md w-full p-4 space-y-3 text-[12px]"
        style={{ fontFamily: 'monospace' }}
      >
        <div className="flex items-center justify-between">
          <div className="text-zinc-100 font-semibold flex items-center gap-2">
            <Send size={14} /> Send Personal Telegram Signal
          </div>
          <button
            data-testid="personal-signal-modal-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200"
          >
            <X size={14} />
          </button>
        </div>

        {isRed && (
          <div
            data-testid="personal-signal-red-warning"
            className="p-2 rounded-sm bg-rose-950/60 border border-rose-700 text-rose-200 text-[11px] flex items-start gap-2"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Badge RED — high risk / skip recommended.</div>
              <label className="mt-1 flex items-center gap-2 text-[10px]">
                <input
                  type="checkbox"
                  data-testid="personal-signal-red-confirm"
                  checked={redConfirmed}
                  onChange={(e) => setRedConfirmed(e.target.checked)}
                />
                I understand the risk and want to send anyway
              </label>
            </div>
          </div>
        )}

        {/* "Signal to send" selector --------------------------------- */}
        <div
          data-testid="signal-to-send-selector"
          className="p-2 rounded-sm border border-zinc-800 bg-zinc-900/40 space-y-2"
        >
          <div className="text-[10px] text-zinc-400 font-semibold tracking-wide">
            SIGNAL TO SEND
          </div>
          <div className="flex flex-wrap gap-2">
            {OPTIONS.map((opt) => {
              const disabled =
                (opt.key === 'under' && underList.length === 0) ||
                (opt.key === 'handicap' && hcList.length === 0);
              return (
                <label
                  key={opt.key}
                  data-testid={`signal-option-${opt.key}`}
                  className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm border ${
                    selectedOption === opt.key
                      ? 'border-violet-600 bg-violet-900/30 text-violet-100'
                      : disabled
                      ? 'border-zinc-800 text-zinc-600 cursor-not-allowed'
                      : 'border-zinc-700 text-zinc-300 cursor-pointer hover:border-zinc-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="signal-to-send"
                    value={opt.key}
                    disabled={disabled}
                    checked={selectedOption === opt.key}
                    onChange={() => setSelectedOption(opt.key)}
                    className="hidden"
                  />
                  {opt.label}
                  {disabled && (
                    <span className="text-[9px] text-zinc-500 ml-1">(n/a)</span>
                  )}
                </label>
              );
            })}
          </div>

          {/* Under line dropdown */}
          {selectedOption === 'under' && (
            <div className="pt-1">
              <label className="text-zinc-500 text-[10px]">Under line (real book)</label>
              {underList.length === 0 ? (
                <div
                  data-testid="under-no-lines"
                  className="text-rose-300 text-[11px] mt-1"
                >
                  No UNDER lines priced by the bookmaker.
                </div>
              ) : (
                <select
                  data-testid="under-line-select"
                  value={underIdx}
                  onChange={(e) => setUnderIdx(Number(e.target.value))}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1 text-zinc-100"
                >
                  {underList.map((c, i) => (
                    <option key={i} value={i}>
                      UNDER {c.line} @ {Number(c.odds).toFixed(2)} · EV{' '}
                      {c.ev_pct != null
                        ? `${c.ev_pct >= 0 ? '+' : ''}${c.ev_pct.toFixed(1)}%`
                        : '—'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Handicap side dropdown */}
          {selectedOption === 'handicap' && (
            <div className="pt-1">
              <label className="text-zinc-500 text-[10px]">Handicap side (real book)</label>
              {hcList.length === 0 ? (
                <div
                  data-testid="hc-no-lines"
                  className="text-rose-300 text-[11px] mt-1"
                >
                  No HC -1.5 sides priced by the bookmaker.
                </div>
              ) : (
                <select
                  data-testid="hc-side-select"
                  value={hcIdx}
                  onChange={(e) => setHcIdx(Number(e.target.value))}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1 text-zinc-100"
                >
                  {hcList.map((c, i) => (
                    <option key={i} value={i}>
                      {c.side} @ {Number(c.odds).toFixed(2)} · EV{' '}
                      {c.ev_pct != null
                        ? `${c.ev_pct >= 0 ? '+' : ''}${c.ev_pct.toFixed(1)}%`
                        : '—'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        {/* Effective pick preview (dynamic) */}
        <div className="grid grid-cols-2 gap-2" data-testid="personal-signal-preview">
          <div>
            <div className="text-zinc-500 text-[10px]">Market</div>
            <div className="text-zinc-100">{effective?.market || '—'}</div>
          </div>
          <div>
            <div className="text-zinc-500 text-[10px]">Side</div>
            <div className="text-zinc-100">{effective?.side || '—'}</div>
          </div>
          {effective?.line != null && (
            <div>
              <div className="text-zinc-500 text-[10px]">Line</div>
              <div className="text-zinc-100">{effective.line}</div>
            </div>
          )}
          <div>
            <div className="text-zinc-500 text-[10px]">Odds</div>
            <div className="text-zinc-100">{fmtNum(effective?.odds)}</div>
          </div>
          <div>
            <div className="text-zinc-500 text-[10px]">Probability</div>
            <div className="text-zinc-100">{fmtNum(effective?.probability_used, 4)}</div>
          </div>
          <div>
            <div className="text-zinc-500 text-[10px]">EV</div>
            <div
              data-testid="personal-signal-ev"
              className={evPct == null ? 'text-zinc-500' : evPct >= 0 ? 'text-emerald-300' : 'text-rose-300'}
            >
              {fmtPct(evPct)}
            </div>
          </div>
          <div>
            <div className="text-zinc-500 text-[10px]">Full Kelly</div>
            <div className="text-zinc-100">
              {kellyFull != null ? `${(kellyFull * 100).toFixed(2)}%` : '—'}
            </div>
          </div>
          <div>
            <div className="text-zinc-500 text-[10px]">Fraction</div>
            <div className="text-zinc-100">{fmtNum(kellyFractionPct, 0)}%</div>
          </div>
          <div className="col-span-2">
            <div className="text-zinc-500 text-[10px]">Suggested stake</div>
            <div className="text-zinc-100">{fmtNum(suggestedUnits, 2)}u</div>
          </div>
        </div>

        {/* EV ≤ 0 warning (non-blocking) */}
        {evNegative && (
          <div
            data-testid="personal-signal-ev-warning"
            className="p-2 rounded-sm bg-amber-950/50 border border-amber-700 text-amber-200 text-[11px] flex items-start gap-2"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              EV negativo ({fmtPct(evPct)}) — il mercato prezza questo pick
              peggio del modello. Procedi solo se hai una convinzione
              manuale forte (es. live-info, bookmaker slow-to-move).
            </div>
          </div>
        )}

        <div>
          <label className="text-zinc-500 text-[10px]">Stake (units, editable)</label>
          <input
            data-testid="personal-signal-stake-input"
            type="number"
            min="0"
            max="10"
            step="0.05"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
            className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1 text-zinc-100"
          />
        </div>

        <div>
          <label className="text-zinc-500 text-[10px]">Operator note (optional)</label>
          <input
            data-testid="personal-signal-note-input"
            type="text"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. confirmed live before send"
            className="w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-1 text-zinc-100"
          />
        </div>

        {err && (
          <div
            data-testid="personal-signal-error"
            className="text-rose-300 text-[11px] px-2 py-1 rounded-sm bg-rose-950/40 border border-rose-800"
          >
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            data-testid="personal-signal-cancel-btn"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="personal-signal-send-btn"
            onClick={handleSend}
            disabled={busy || underDisabled || hcDisabled || (isRed && !redConfirmed)}
            className="px-3 py-1.5 rounded-sm bg-violet-700 hover:bg-violet-600 text-white disabled:opacity-40 flex items-center gap-1"
          >
            <Send size={12} /> {busy ? 'Sending…' : 'Send to Personal Telegram'}
          </button>
        </div>
      </div>
    </div>
  );
}
